import type { WeComScheduleBoardItem } from './api'

export const weekdayNames = ['', '一', '二', '三', '四', '五', '六', '日']
const dayMilliseconds = 24 * 60 * 60 * 1000

export type DayScheduleIndicator = 'none' | 'scheduled' | 'has_leave'

export interface CalendarDayItem {
  date: string // 'YYYY-MM-DD'
  dayNumber: number
  month: number
  year: number
  weekday: number // 1..7
  weekdayName: string // '一'..'日'
  isToday: boolean
  isCurrentWeek: boolean
  status: DayScheduleIndicator
  scheduleCount: number
}

export interface TwoWeekCalendarModel {
  currentDate: string
  monthLabel: string
  days: CalendarDayItem[]
}

export interface ScheduleBoardCardItem extends WeComScheduleBoardItem {
  assistantLabel: string
  regionalManagerLabel: string
  timeRangeLabel: string | null
}

export interface ScheduleBoardTimelineItem extends WeComScheduleBoardItem {
  assistantLabel: string
  regionalManagerLabel: string
}

export interface ScheduleBoardTimelineGroup {
  datetime: string
  items: ScheduleBoardTimelineItem[]
  monthDayLabel: string
  weekdayLabel: string
}

export const scheduleStatusLabels = {
  CANCELLED: '已取消',
  OBSOLETE: '已废弃',
  SCHEDULED: '正常出诊',
} as const

export function getBeijingDate(): string {
  const dateParts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
  }).formatToParts(new Date())
  const values = Object.fromEntries(dateParts.map(part => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}

function dateToUtcDay(date: string) {
  const [year, month, day] = date.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

function utcDayToDateString(utcTimestamp: number) {
  const date = new Date(utcTimestamp)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function getWeekStart(date: string) {
  const utcDay = new Date(dateToUtcDay(date)).getUTCDay()
  const daysSinceMonday = utcDay === 0 ? 6 : utcDay - 1
  return dateToUtcDay(date) - daysSinceMonday * dayMilliseconds
}

export function formatMonthDayLabel(date: string) {
  const [, month, day] = date.split('-')
  return `${Number(month)}月${Number(day)}日`
}

export function formatWeekdayLabel(date: string, weekday: number, currentDate: string) {
  const weekOffset = Math.round((getWeekStart(date) - getWeekStart(currentDate)) / (7 * dayMilliseconds))
  const weekPrefix = weekOffset === 0
    ? '本'
    : weekOffset > 0
      ? '下'.repeat(weekOffset)
      : '上'.repeat(Math.abs(weekOffset))
  return `${weekPrefix}周${weekdayNames[weekday]}`
}

export type ScheduleBoardItemInput = Omit<WeComScheduleBoardItem, 'assistantNames' | 'assistantUserIds'> & {
  assistantNames: readonly string[]
  assistantUserIds: readonly string[]
}

export function filterRelatedScheduleBoardItems(
  items: readonly ScheduleBoardItemInput[],
  currentUserId: string,
): ScheduleBoardItemInput[] {
  return items.filter(item => item.regionalManagerUserId === currentUserId
    || item.assistantUserIds.includes(currentUserId))
}
export function toCardItem(item: ScheduleBoardItemInput): ScheduleBoardCardItem {
  const timeRangeLabel = item.startTime && item.endTime
    ? `${item.startTime} - ${item.endTime}`
    : item.startTime
      ? `${item.startTime} 开始`
      : null

  return {
    ...item,
    assistantNames: [...item.assistantNames],
    assistantUserIds: [...item.assistantUserIds],
    assistantLabel: item.assistantNames.join('、') || '无',
    regionalManagerLabel: item.regionalManagerName ?? '未配置',
    timeRangeLabel,
  }
}

export function buildTwoWeekCalendar(
  items: readonly ScheduleBoardItemInput[],
  currentDate = getBeijingDate(),
): TwoWeekCalendarModel {
  const currentWeekStartTimestamp = getWeekStart(currentDate)

  // Map items by date
  const dateItemsMap = new Map<string, ScheduleBoardItemInput[]>()
  for (const item of items) {
    const list = dateItemsMap.get(item.date) ?? []
    list.push(item)
    dateItemsMap.set(item.date, list)
  }

  const days: CalendarDayItem[] = []
  for (let i = 0; i < 14; i++) {
    const dayTimestamp = currentWeekStartTimestamp + i * dayMilliseconds
    const dateStr = utcDayToDateString(dayTimestamp)
    const [yStr, mStr, dStr] = dateStr.split('-')
    const year = Number(yStr)
    const month = Number(mStr)
    const dayNumber = Number(dStr)
    const weekday = (i % 7) + 1 // 1..7 (Mon..Sun)
    const isToday = dateStr === currentDate
    const isCurrentWeek = i < 7

    const dayItems = dateItemsMap.get(dateStr) ?? []
    let status: DayScheduleIndicator = 'none'
    if (dayItems.length > 0) {
      const hasAbnormal = dayItems.some(item => item.status === 'CANCELLED')
      status = hasAbnormal ? 'has_leave' : 'scheduled'
    }

    days.push({
      date: dateStr,
      dayNumber,
      isCurrentWeek,
      isToday,
      month,
      scheduleCount: dayItems.length,
      status,
      weekday,
      weekdayName: weekdayNames[weekday],
      year,
    })
  }

  // Determine header month label from currentDate
  const [curYear, curMonth] = currentDate.split('-')
  const monthLabel = `${Number(curYear)}年${Number(curMonth)}月`

  return {
    currentDate,
    days,
    monthLabel,
  }
}

export function groupItemsByDate(
  items: readonly ScheduleBoardItemInput[],
): Map<string, ScheduleBoardCardItem[]> {
  const map = new Map<string, ScheduleBoardCardItem[]>()
  for (const item of items) {
    const list = map.get(item.date) ?? []
    list.push(toCardItem(item))
    map.set(item.date, list)
  }
  return map
}

// Keep backward compatibility for existing imports if any
export function createScheduleBoardTimelineGroups(
  items: readonly ScheduleBoardItemInput[],
  currentDate = getBeijingDate(),
): ScheduleBoardTimelineGroup[] {
  const groups = new Map<string, ScheduleBoardTimelineGroup>()
  for (const item of items) {
    let group = groups.get(item.date)
    if (!group) {
      group = {
        datetime: item.date,
        items: [],
        monthDayLabel: formatMonthDayLabel(item.date),
        weekdayLabel: formatWeekdayLabel(item.date, item.weekday, currentDate),
      }
      groups.set(item.date, group)
    }
    group.items.push(toCardItem(item))
  }
  return [...groups.values()]
}
